import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Cross } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const AGE_BRACKETS = ["0-3 ans", "4-12 ans", "13-18 ans", "19-35 ans", "36-60 ans", "60+ ans"];
const PIE_COLORS = ["#C62828", "#D96C4A", "#E3A72F", "#1E4631", "#5C6B61", "#2E7D32", "#7B1FA2", "#6D4C41"];

export default function Deaths() {
  const [data, setData] = useState({ items: [], total: 0, by_age_bracket: {}, by_cause: {} });
  useEffect(() => {
    axios.get(`${API}/deaths`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const ageChart = AGE_BRACKETS.map((b) => ({ tranche: b, Décès: data.by_age_bracket[b] || 0 }));
  const causeData = Object.entries(data.by_cause).map(([k, v]) => ({ name: k, value: v }));
  const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="space-y-6" data-testid="deaths-page">
      <div>
        <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
          <Cross className="w-7 h-7 text-[#C62828]"/> Registre des décès
        </h1>
        <p className="text-[#5C6B61] mt-1 text-sm">Suivi médical des décès enregistrés à l'hôpital · {data.total} total</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5">
          <div className="text-xs text-[#5C6B61] uppercase">Total décès</div>
          <div className="text-4xl font-[Outfit] font-bold mt-2 text-[#C62828]">{data.total}</div>
          <div className="text-xs text-[#5C6B61] mt-2 italic">Données confidentielles · usage médical uniquement</div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5">
          <h3 className="font-[Outfit] font-semibold mb-3">Par tranche d'âge</h3>
          <div className="h-44">
            {data.total > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ageChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                  <XAxis dataKey="tranche" tick={{ fontSize: 10, fill: "#5C6B61" }}/>
                  <YAxis tick={{ fontSize: 10, fill: "#5C6B61" }} allowDecimals={false}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }}/>
                  <Bar dataKey="Décès" fill="#C62828" radius={[6, 6, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-xs text-[#5C6B61]">Aucune donnée</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5">
          <h3 className="font-[Outfit] font-semibold mb-3">Par cause</h3>
          <div className="h-44">
            {causeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={causeData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={60}>
                    {causeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 11 }}/>
                  <Legend wrapperStyle={{ fontSize: 10 }}/>
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-xs text-[#5C6B61]">Aucune donnée</div>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61] tracking-wider">
            <tr>
              <th className="px-5 py-3">Patient</th>
              <th className="px-5 py-3">Âge</th>
              <th className="px-5 py-3">Tranche</th>
              <th className="px-5 py-3">Cause</th>
              <th className="px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-[#5C6B61]" data-testid="empty-deaths">Aucun décès enregistré</td></tr>
            ) : data.items.map((p) => {
              const age = p.age;
              const bracket = age <= 3 ? "0-3 ans" : age <= 12 ? "4-12 ans" : age <= 18 ? "13-18 ans" : age <= 35 ? "19-35 ans" : age <= 60 ? "36-60 ans" : "60+ ans";
              return (
                <tr key={p.id} className="border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50" data-testid={`death-row-${p.id}`}>
                  <td className="px-5 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-[#5C6B61]">{p.phone}</div></td>
                  <td className="px-5 py-3">{age}</td>
                  <td className="px-5 py-3 text-xs"><span className="px-2 py-0.5 rounded-full bg-[#C62828]/10 text-[#C62828]">{bracket}</span></td>
                  <td className="px-5 py-3 text-[#5C6B61] text-xs max-w-[300px]">{p.death_cause}{p.death_notes ? <div className="italic mt-1">{p.death_notes}</div> : null}</td>
                  <td className="px-5 py-3 text-xs text-[#5C6B61]">{fmt(p.deceased_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
